import { Injectable } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { ItemStatus, ItemStatusMetadata } from "@common/ADAPT.Common.Model/organisation/item-status";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { UserService } from "@common/user/user.service";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityHandler } from "@org-common/lib/activities/activity-handler";
import { NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { KanbanService } from "@org-common/lib/kanban/kanban.service";
import moment from "moment";
import { lastValueFrom } from "rxjs";
import { filter, map } from "rxjs/operators";

@Injectable()
export class ActionActivityHandler extends ActivityHandler<Item> {
    public readonly entityType = Item;
    public readonly activityTypes = [NonCulturalActivityType.Actions];

    constructor(
        private kanbanService: KanbanService,
        private rxjsBreezeService: RxjsBreezeService,
        private userService: UserService,
    ) {
        super();
    }

    public get shouldUpdate$() {
        return this.rxjsBreezeService.entityTypeChanged(Item).pipe(
            filter((item) => item.assigneeId === this.userService.currentPerson?.personId),
            map(() => [NonCulturalActivityType.Actions]),
        );
    }

    public async getGroupedActivities(personId: number, startDate?: Date, endDate?: Date, excludeCompleted?: boolean) {
        const group = ActivitiesService.generateActivitiesGroupObject();

        const predicate = new MethodologyPredicate<Item>();

        if (excludeCompleted) {
            predicate.and(new MethodologyPredicate<Item>("status", "in", [ItemStatus.ToDo, ItemStatus.InProgress]));
        } else {
            predicate.and(new MethodologyPredicate<Item>("status", "in", [ItemStatus.ToDo, ItemStatus.InProgress, ItemStatus.Done, ItemStatus.Closed]));
        }

        if (startDate) {
            predicate.and(new MethodologyPredicate<Item>("dueDate", "==", null)
                .or(new MethodologyPredicate<Item>("dueDate", ">=", startDate)));
        }

        if (endDate) {
            predicate.and(new MethodologyPredicate<Item>("dueDate", "==", null)
                .or(new MethodologyPredicate<Item>("dueDate", "<=", endDate)));
        }

        const items = await lastValueFrom(this.kanbanService.getItemsByPersonId(personId, predicate, excludeCompleted));
        items.sort(SortUtilities.getSortByFieldFunction("dueDate"));
        group.allItems = items;

        // only want to include items with dueDate set in the overdue/upcoming groups
        const itemsWithDueDate = items.filter((i) => !!i.dueDate);
        for (const item of itemsWithDueDate) {
            const status = getActionStatus(item);
            group[status].push(item);
        }

        group.totalCount = items.length;
        group.totalCountByStatus = itemsWithDueDate.reduce((counts, item) => {
            counts[getActionStatus(item)] += 1;
            return counts;
        }, ActivitiesService.generateActivitiesGroupedByStatusCountObject());

        return { [NonCulturalActivityType.Actions]: group };

        function getActionStatus(item: Item) {
            const isOverdue = moment().startOf("day").isSameOrAfter(item.dueDate);
            return isOverdue ? CulturalActivityStatus.Overdue : CulturalActivityStatus.Upcoming;
        }
    }

    public async getCalendarEventForActivity(activity: Item) {
        let title = activity.summary;
        const completed = activity.status === ItemStatus.Done || activity.status === ItemStatus.Closed;
        if (completed) {
            const statusMeta = ItemStatusMetadata.ByStatus[activity.status];
            title = `(${statusMeta.name}) ${title}`;
        }

        return {
            title,
            startDate: activity.dueDate,
            endDate: undefined,
            completed,
        };
    }
}
